import * as Dialog from '@radix-ui/react-dialog'
import * as DM from '@radix-ui/react-dropdown-menu'
import * as PO from '@radix-ui/react-popover'
import * as TT from '@radix-ui/react-tooltip'
import { IconCheck, IconX } from '@tabler/icons-react'
import { forwardRef, type ReactElement, type ReactNode } from 'react'
import { IconButton } from './Button'
import { cx } from './primitives'

const SIZES = { xs: 380, sm: 460, md: 560, lg: 720, xl: 960, xxl: 1120 } as const
export type ModalSize = keyof typeof SIZES | number

export interface ModalProps {
  opened: boolean
  onClose: () => void
  title?: ReactNode
  /** Başlık görünmese de ekran okuyucu için ad. */
  ariaLabel?: string
  size?: ModalSize
  /** Üstten hizalı (uzun formlar) — varsayılan ortalı. */
  top?: boolean
  fullScreen?: boolean
  withCloseButton?: boolean
  /** Dışarı tıklama / Esc ile kapanmasın (kaydedilmemiş iş). */
  dismissible?: boolean
  footer?: ReactNode
  children?: ReactNode
  className?: string
  /** Gövde dolgusu olmadan (kendi düzenini kuran içerik). */
  bare?: boolean
}

/** Pencere: odak kilidi, Esc, odak geri dönüşü Radix Dialog'tan. */
export function Modal({
  opened, onClose, title, ariaLabel, size = 'md', top, fullScreen, withCloseButton = true, dismissible = true,
  footer, children, className, bare,
}: ModalProps) {
  const width = typeof size === 'number' ? size : SIZES[size]
  return (
    <Dialog.Root open={opened} onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-overlay" />
        <Dialog.Content
          className={cx('ui-modal', top && 'ui-modal--top', fullScreen && 'ui-modal--full', className)}
          style={fullScreen ? undefined : { maxWidth: width }}
          aria-describedby={undefined}
          onInteractOutside={(e) => { if (!dismissible) e.preventDefault() }}
          onEscapeKeyDown={(e) => { if (!dismissible) e.preventDefault() }}>
          {title || withCloseButton ? (
            <div className="ui-modal__header">
              {title ? <Dialog.Title className="ui-modal__title">{title}</Dialog.Title>
                : <Dialog.Title className="ui-visually-hidden">{ariaLabel ?? 'Pencere'}</Dialog.Title>}
              {withCloseButton && (
                <Dialog.Close asChild><IconButton label="Kapat"><IconX size={18} /></IconButton></Dialog.Close>
              )}
            </div>
          ) : (
            <Dialog.Title className="ui-visually-hidden">{ariaLabel ?? 'Pencere'}</Dialog.Title>
          )}
          {bare ? children : <div className="ui-modal__body">{children}</div>}
          {footer && <div className="ui-modal__footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export interface DrawerProps {
  opened: boolean
  onClose: () => void
  title?: ReactNode
  /** Başlık şeridinde başlığın solunda (dosya rozeti vb.). */
  icon?: ReactNode
  subtitle?: ReactNode
  position?: 'right' | 'left'
  width?: number
  children?: ReactNode
  ariaLabel?: string
  /** Başlığın üstündeki satır (kod, rozetler); verilirse başlık büyük ve alt satırda durur. */
  eyebrow?: ReactNode
  /** Kaydırılmayan alt çubuk (eylem düğmeleri). */
  footer?: ReactNode
}

/** Yan çekmece (sürüm geçmişi, açık oturumlar, mobil menü). */
export function Drawer({ opened, onClose, title, icon, subtitle, position = 'right', width = 460, children, ariaLabel, eyebrow, footer }: DrawerProps) {
  const close = <Dialog.Close asChild><IconButton label="Kapat"><IconX size={18} /></IconButton></Dialog.Close>
  return (
    <Dialog.Root open={opened} onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-overlay" />
        <Dialog.Content className={cx('ui-drawer', position === 'left' && 'ui-drawer--left')} style={{ maxWidth: width }} aria-describedby={undefined}>
          {title && eyebrow ? (
            <div className="ui-drawer__header ui-drawer__header--stack">
              <div className="ui-drawer__eyebrow">{eyebrow}<span style={{ flex: 1 }} />{close}</div>
              {subtitle && <span className="ui-drawer__sub">{subtitle}</span>}
              <Dialog.Title className="ui-drawer__title--lg">{title}</Dialog.Title>
            </div>
          ) : title ? (
            <div className="ui-drawer__header">
              {icon}
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Dialog.Title style={{ margin: 0, fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Dialog.Title>
                {subtitle && <span style={{ fontSize: 12, color: 'var(--nx-fg-muted)' }}>{subtitle}</span>}
              </span>
              <Dialog.Close asChild><IconButton label="Kapat"><IconX size={18} /></IconButton></Dialog.Close>
            </div>
          ) : <Dialog.Title className="ui-visually-hidden">{ariaLabel ?? 'Çekmece'}</Dialog.Title>}
          <div className="ui-drawer__body">{children}</div>
          {footer && <div className="ui-drawer__footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/* ------------------------------------------------------------------- menü */

export interface MenuProps {
  /** Tetikleyici: tek bir odaklanabilir öğe (IconButton, Button). */
  trigger: ReactElement
  children: ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'bottom' | 'left' | 'right'
  width?: number
  open?: boolean
  onOpenChange?: (o: boolean) => void
  /** false: kapanınca odak tetikleyiciye dönmez (editör araç çubuğu: odak belgede kalsın). */
  restoreFocus?: boolean
}

export function Menu({ trigger, children, align = 'end', side = 'bottom', width, open, onOpenChange, restoreFocus = true }: MenuProps) {
  return (
    <DM.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        <DM.Content className="ui-menu" align={align} side={side} sideOffset={6} collisionPadding={8} style={width ? { width } : undefined}
          onCloseAutoFocus={restoreFocus ? undefined : (e) => e.preventDefault()}>
          {children}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  )
}

interface MenuItemProps {
  icon?: ReactNode
  onSelect?: () => void
  danger?: boolean
  disabled?: boolean
  right?: ReactNode
  children: ReactNode
  /** Bağlantı olarak çizilecekse (asChild ile <Link>). */
  asChild?: boolean
}

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(function MenuItem(
  { icon, onSelect, danger, disabled, right, children, asChild }, ref,
) {
  if (asChild) {
    return <DM.Item ref={ref} asChild disabled={disabled} onSelect={onSelect} className={cx('ui-menu-item', danger && 'ui-menu-item--danger')}>{children}</DM.Item>
  }
  return (
    <DM.Item ref={ref} disabled={disabled} onSelect={onSelect} className={cx('ui-menu-item', danger && 'ui-menu-item--danger')}>
      {icon}<span style={{ flex: 1 }}>{children}</span>{right && <span className="ui-menu-item__right">{right}</span>}
    </DM.Item>
  )
})

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DM.Label className="ui-menu-label">{children}</DM.Label>
}
export function MenuDivider() {
  return <DM.Separator className="ui-menu-sep" />
}
/** Tek seçimli menü grubu (blok türü, hizalama): öğeler role="menuitemradio". */
export function MenuRadioGroup({ value, onValueChange, children }: { value: string; onValueChange: (v: string) => void; children: ReactNode }) {
  return <DM.RadioGroup value={value} onValueChange={onValueChange}>{children}</DM.RadioGroup>
}
export function MenuRadioItem({ value, children, icon, right }: { value: string; children: ReactNode; icon?: ReactNode; right?: ReactNode }) {
  return (
    <DM.RadioItem value={value} className="ui-menu-item">
      {icon}<span style={{ flex: 1 }}>{children}</span>
      {right}
      <DM.ItemIndicator style={{ display: 'flex' }}><IconCheck size={14} aria-hidden="true" /></DM.ItemIndicator>
    </DM.RadioItem>
  )
}

export function MenuCheckItem({
  checked, onSelect, children, icon,
}: { checked: boolean; onSelect: () => void; children: ReactNode; icon?: ReactNode }) {
  return (
    <DM.CheckboxItem checked={checked} onSelect={onSelect} className="ui-menu-item">
      {icon}<span style={{ flex: 1 }}>{children}</span>
      <DM.ItemIndicator><span aria-hidden="true">✓</span></DM.ItemIndicator>
    </DM.CheckboxItem>
  )
}

/* ---------------------------------------------------------- ipucu, balon */

export function TooltipProvider({ children }: { children: ReactNode }) {
  return <TT.Provider delayDuration={400} skipDelayDuration={200}>{children}</TT.Provider>
}

/** İpucu: tetikleyicinin adı zaten varsa yalnız ek bilgi verir. */
export function Tooltip({
  label, children, side = 'top', disabled,
}: { label: ReactNode; children: ReactElement; side?: 'top' | 'bottom' | 'left' | 'right'; disabled?: boolean }) {
  if (disabled || !label) return children
  return (
    <TT.Root>
      <TT.Trigger asChild>{children}</TT.Trigger>
      <TT.Portal>
        <TT.Content className="ui-tooltip" side={side} sideOffset={6} collisionPadding={8}>{label}</TT.Content>
      </TT.Portal>
    </TT.Root>
  )
}

export function Popover({
  trigger, children, open, onOpenChange, align = 'start', width,
}: { trigger: ReactElement; children: ReactNode; open?: boolean; onOpenChange?: (o: boolean) => void; align?: 'start' | 'center' | 'end'; width?: number }) {
  return (
    <PO.Root open={open} onOpenChange={onOpenChange}>
      <PO.Trigger asChild>{trigger}</PO.Trigger>
      <PO.Portal>
        <PO.Content className="ui-popover" align={align} sideOffset={6} collisionPadding={8} style={width ? { width } : undefined}>{children}</PO.Content>
      </PO.Portal>
    </PO.Root>
  )
}
